/**
 * 🎨 Layout Floating Toolbar - Widget Layout System
 * 
 * Floating Toolbar für Widget Layout-Einstellungen
 * Ermöglicht Inline-Editing von Widget-Layout-Properties
 * 
 * Features:
 * - Background: Solid Color, Gradient, Background Image
 * - Spacing: Padding & Margin (Desktop/Mobile) mit Toggles
 * - Dimensions: Width, Height, Min-Width, Min-Height (Desktop/Mobile) mit Toggles
 * - Flex Direction: row, column, row-reverse, column-reverse
 * - Child Distribution: Prozentuale Platzverteilung (z.B. 50/50, 60/40, 70/30)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './LayoutFloatingToolbar.css';

function LayoutFloatingToolbar({
    // Required Props
    isEditMode,
    isVisible,
    
    // 🎨 Background Props
    backgroundColor = '#ffffff',
    backgroundOpacity = 100, // ✅ NEW: Background Opacity for solid colors
    backgroundGradientEnabled = false,
    backgroundGradientColor1 = '#667eea',
    backgroundGradientColor2 = '#764ba2',
    backgroundGradientAnimation = false,
    backgroundGradientDirection = 'horizontal', // ✅ NEW: Animation Direction (horizontal, vertical, diagonal-left, diagonal-right)
    backgroundImage = null,
    backgroundImageOpacity = 100,
    backgroundImageSize = 'cover',
    backgroundImagePosition = 'center',
    
    // Padding (Desktop)
    paddingTop = 0,
    paddingRight = 0,
    paddingBottom = 0,
    paddingLeft = 0,
    
    // Padding (Mobile)
    paddingTopMobile,
    paddingRightMobile,
    paddingBottomMobile,
    paddingLeftMobile,
    
    // Margin (Desktop)
    marginTop = 0,
    marginRight = 0,
    marginBottom = 0,
    marginLeft = 0,
    
    // Margin (Mobile)
    marginTopMobile,
    marginRightMobile,
    marginBottomMobile,
    marginLeftMobile,
    
    // Dimensions (Desktop)
    width,
    height,
    minWidth,
    minHeight,
    
    // Dimensions (Mobile)
    widthMobile,
    heightMobile,
    minWidthMobile,
    minHeightMobile,
    
    // Layout Settings (Desktop)
    flexDirection = 'row', // row | column | row-reverse | column-reverse
    childDistribution = '50-50', // '50-50' | '60-40' | '70-30' | '40-60' | '30-70' | 'auto'
    
    // Layout Settings (Mobile)
    flexDirectionMobile,
    childDistributionMobile,
    
    gridLayout = 'grid-cols-2-equal', // Grid Layout für Dynamic Containers
    containerCount = 2, // Anzahl der Container (1-5)
    
    // 🎨 Border Settings
    borderTopEnabled = false,
    borderTopWidth = 1,
    borderTopColor = '#cccccc',
    borderBottomEnabled = false,
    borderBottomWidth = 1,
    borderBottomColor = '#cccccc',
    borderLeftEnabled = false,
    borderLeftWidth = 1,
    borderLeftColor = '#cccccc',
    borderRightEnabled = false,
    borderRightWidth = 1,
    borderRightColor = '#cccccc',
    
    // Callbacks
    onStyleChange, // (styles) => void - komplette Style Updates
    colors = {}, // Brand Colors
    onClose, // () => void - Callback zum Schließen
    
    // Additional Props
    widgetRef, // Ref zum Widget Element für Position-Tracking
    
    // Responsive
    isMobileLayout = false
}) {
    // 🎨 Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop'); // ✅ FIX: Initialisiere mit isMobileLayout
    const [expandedSection, setExpandedSection] = useState(null); // 'background' | 'spacing' | 'dimensions' | 'layout' | 'style' | null
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Spacing Sub-Section State (padding/margin)
    const [spacingType, setSpacingType] = useState('padding'); // 'padding' | 'margin'
    
    // 🎨 Custom Color Slots (shared mit TipTap via LocalStorage)
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    // 🎨 Image Upload State
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    
    const toolbarRef = useRef(null);
    
    // ✅ Save Custom Colors to LocalStorage
    useEffect(() => {
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(customColorSlots));
        } catch (error) {
            console.error('Failed to save custom color slots:', error);
        }
    }, [customColorSlots]);
    
    // ✅ Sync Custom Colors beim Öffnen der Background Section
    useEffect(() => {
        if (expandedSection === 'background') {
            try {
                const stored = localStorage.getItem('tiptap-custom-color-slots');
                if (stored) {
                    const storedSlots = JSON.parse(stored);
                    if (JSON.stringify(storedSlots) !== JSON.stringify(customColorSlots)) {
                        setCustomColorSlots(storedSlots);
                    }
                }
            } catch (error) {
                console.error('Failed to sync custom color slots:', error);
            }
        }
    }, [expandedSection]);
    
    // 🎯 Synchronisiere Device Toggle mit isMobileLayout - WICHTIG!
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // ✅ Show/Hide Toolbar based on isVisible prop
    useEffect(() => {
        if (isVisible) {
            setShowToolbar(true);
            // Nur Position updaten wenn NICHT manuell verschoben
            if (!isManuallyPositioned) {
                setTimeout(() => {
                    updateToolbarPosition();
                }, 50);
            }
        } else {
            setExpandedSection(null); // Reset expanded section when closing
            setTimeout(() => {
                setShowToolbar(false);
            }, 200); // Warten auf Fade-Out Animation
        }
    }, [isVisible]);
    
    // ✅ Update Position when widget moves or resizes
    const updateToolbarPosition = () => {
        if (!widgetRef || !widgetRef.current || !toolbarRef.current) return;
        
        const widgetRect = widgetRef.current.getBoundingClientRect();
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        
        // Position: Oben mittig über dem Widget
        const left = widgetRect.left + (widgetRect.width / 2) - (toolbarRect.width / 2);
        const top = widgetRect.top - toolbarRect.height - 20; // 20px Abstand
        
        setToolbarPosition({ top, left });
    };
    
    // ✅ Update Position on Resize (aber nur wenn nicht manuell verschoben)
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResize = () => {
            if (!expandedSection) {
                updateToolbarPosition();
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [showToolbar, expandedSection, isManuallyPositioned]);
    
    // ✅ Handle Outside Click - Close toolbar
    useEffect(() => {
        if (!showToolbar) return;
        
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInWidget = widgetRef && widgetRef.current && widgetRef.current.contains(event.target);
            
            // Klick war außerhalb von Toolbar UND Widget
            if (!clickedInToolbar && !clickedInWidget) {
                // Wenn eine Section geöffnet ist, erst diese schließen
                if (expandedSection) {
                    setExpandedSection(null);
                } else {
                    // Sonst direkt Toolbar schließen
                    if (onClose) {
                        onClose();
                    }
                }
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolbar, onClose, expandedSection, widgetRef]);
    
    // 🎯 Drag Handlers - MUSS VOR early return stehen!
    const handleMouseDown = (e) => {
        // Verhindere Drag wenn auf Close-Button geklickt
        if (e.target.closest('.layout-close-btn')) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 300);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true); // ✅ Mark as manually positioned
    };
    
    // ✅ Drag Event Listeners - MUSS VOR early return stehen!
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // 🚫 Don't render if not in edit mode or not visible
    if (!isEditMode || !isVisible || !showToolbar) {
        return null;
    }
    
    // 🎨 Active Padding/Margin (responsive)
    const activePaddingTop = device === 'mobile' 
        ? (paddingTopMobile !== undefined && paddingTopMobile !== null ? paddingTopMobile : paddingTop)
        : paddingTop;
    
    const activePaddingRight = device === 'mobile' 
        ? (paddingRightMobile !== undefined && paddingRightMobile !== null ? paddingRightMobile : paddingRight)
        : paddingRight;
    
    const activePaddingBottom = device === 'mobile' 
        ? (paddingBottomMobile !== undefined && paddingBottomMobile !== null ? paddingBottomMobile : paddingBottom)
        : paddingBottom;
    
    const activePaddingLeft = device === 'mobile' 
        ? (paddingLeftMobile !== undefined && paddingLeftMobile !== null ? paddingLeftMobile : paddingLeft)
        : paddingLeft;
    
    const activeMarginTop = device === 'mobile' 
        ? (marginTopMobile !== undefined && marginTopMobile !== null ? marginTopMobile : marginTop)
        : marginTop;
    
    const activeMarginRight = device === 'mobile' 
        ? (marginRightMobile !== undefined && marginRightMobile !== null ? marginRightMobile : marginRight)
        : marginRight;
    
    const activeMarginBottom = device === 'mobile' 
        ? (marginBottomMobile !== undefined && marginBottomMobile !== null ? marginBottomMobile : marginBottom)
        : marginBottom;
    
    const activeMarginLeft = device === 'mobile' 
        ? (marginLeftMobile !== undefined && marginLeftMobile !== null ? marginLeftMobile : marginLeft)
        : marginLeft;
    
    // 🎨 Update Handler
    const handleUpdate = (updates) => {
        if (onStyleChange) {
            onStyleChange(updates);
        }
    };
    
    // 🎨 Padding Update
    const handlePaddingChange = (side, value) => {
        const propName = device === 'mobile' 
            ? `padding${side.charAt(0).toUpperCase() + side.slice(1)}Mobile`
            : `padding${side.charAt(0).toUpperCase() + side.slice(1)}`;
        
        handleUpdate({ [propName]: Number(value) });
    };
    
    // 🎨 Margin Update
    const handleMarginChange = (side, value) => {
        const propName = device === 'mobile' 
            ? `margin${side.charAt(0).toUpperCase() + side.slice(1)}Mobile`
            : `margin${side.charAt(0).toUpperCase() + side.slice(1)}`;
        
        handleUpdate({ [propName]: Number(value) });
    };
    
    // 🎨 Dimensions Update Handlers
    const handleDimensionChange = (dimension, value) => {
        const propName = device === 'mobile' ? `${dimension}Mobile` : dimension;
        handleUpdate({ [propName]: value === 'auto' ? 'auto' : Number(value) });
    };
    
    const handleDimensionToggle = (dimension, enabled) => {
        const propName = device === 'mobile' ? `${dimension}Mobile` : dimension;
        if (enabled) {
            // Enable: Set to a default numeric value
            const defaultValues = {
                width: 800,
                height: 500,
                minWidth: 300,
                minHeight: 200
            };
            handleUpdate({ [propName]: defaultValues[dimension] || 500 });
        } else {
            // Disable: Set to 'auto'
            handleUpdate({ [propName]: 'auto' });
        }
    };
    
    // 🎨 Active Dimensions (responsive)
    const getActiveDimension = (desktopValue, mobileValue) => {
        if (device === 'mobile') {
            return mobileValue !== undefined && mobileValue !== null ? mobileValue : desktopValue;
        }
        return desktopValue;
    };
    
    const activeWidth = getActiveDimension(width, widthMobile);
    const activeHeight = getActiveDimension(height, heightMobile);
    const activeMinWidth = getActiveDimension(minWidth, minWidthMobile);
    const activeMinHeight = getActiveDimension(minHeight, minHeightMobile);
    
    // Check if dimensions are enabled (not 'auto')
    const isWidthEnabled = activeWidth !== 'auto' && activeWidth !== undefined && activeWidth !== null;
    const isHeightEnabled = activeHeight !== 'auto' && activeHeight !== undefined && activeHeight !== null;
    const isMinWidthEnabled = activeMinWidth !== 'auto' && activeMinWidth !== undefined && activeMinWidth !== null;
    const isMinHeightEnabled = activeMinHeight !== 'auto' && activeMinHeight !== undefined && activeMinHeight !== null;
    
    // 🎨 Active Layout Values (responsive)
    const activeFlexDirection = device === 'mobile'
        ? (flexDirectionMobile ?? flexDirection)
        : flexDirection;
    
    const activeChildDistribution = device === 'mobile'
        ? (childDistributionMobile ?? childDistribution)
        : childDistribution;
    
    // 🎨 Flex Direction Update (responsive)
    const handleFlexDirectionChange = (direction) => {
        console.log('🎯 [LAYOUT TOOLBAR] Flex Direction changed:', {
            direction,
            device,
            isMobileLayout
        });
        
        if (device === 'mobile') {
            handleUpdate({ flexDirectionMobile: direction });
        } else {
            handleUpdate({ flexDirection: direction });
        }
    };
    
    // 🎨 Child Distribution Update (responsive)
    const handleChildDistributionChange = (distribution) => {
        console.log('🎯 [LAYOUT TOOLBAR] Child Distribution changed:', {
            distribution,
            device,
            isMobileLayout
        });
        
        if (device === 'mobile') {
            handleUpdate({ childDistributionMobile: distribution });
        } else {
            handleUpdate({ childDistribution: distribution });
        }
    };
    
    // 🎨 Grid Layout Update
    const handleGridLayoutChange = (layout) => {
        handleUpdate({ gridLayout: layout });
    };
    
    // 🎨 Background Handlers
    const handleBackgroundColorChange = (color) => {
        handleUpdate({ 
            backgroundColor: color,
            backgroundGradientEnabled: false // Disable gradient when solid color is selected
        });
    };
    
    const handleGradientToggle = (enabled) => {
        handleUpdate({ backgroundGradientEnabled: enabled });
    };
    
    const handleGradientColor1Change = (color) => {
        handleUpdate({ backgroundGradientColor1: color });
    };
    
    const handleGradientColor2Change = (color) => {
        handleUpdate({ backgroundGradientColor2: color });
    };
    
    const handleGradientAnimationToggle = (enabled) => {
        handleUpdate({ backgroundGradientAnimation: enabled });
    };
    
    const handleGradientDirectionChange = (direction) => {
        handleUpdate({ backgroundGradientDirection: direction });
    };
    
    const handleBackgroundOpacityChange = (value) => {
        handleUpdate({ backgroundOpacity: Number(value) });
    };
    
    const handleBackgroundImageUpload = async () => {
        console.log('🖼️ Background Image Upload triggered');
        
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        
        input.onchange = async (e) => {
            console.log('📁 File selected:', e.target.files);
            const file = e.target.files?.[0];
            
            if (!file) {
                console.log('⚠️ No file selected');
                return;
            }
            
            // File Size Check (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File is too large. Maximum size is 5MB.');
                return;
            }
            
            console.log('✅ File found:', file.name, file.size, file.type);
            setIsUploadingImage(true);
            
            try {
                // ✅ FIREBASE STORAGE UPLOAD
                const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
                const { getAuth } = await import('firebase/auth');
                
                const storage = getStorage();
                const auth = getAuth();
                
                if (!auth.currentUser) {
                    alert('You must be logged in to upload images.');
                    setIsUploadingImage(false);
                    return;
                }
                
                const userId = auth.currentUser.uid;
                const timestamp = Date.now();
                const randomId = Math.random().toString(36).substr(2, 9);
                const uniqueFileName = `${timestamp}_${randomId}-${file.name}`;
                
                // Upload to Firebase Storage
                const imageRef = storageRef(storage, `pages/${userId}/widget-backgrounds/${uniqueFileName}`);
                const uploadTask = uploadBytesResumable(imageRef, file);

                uploadTask.on('state_changed',
                    (snapshot) => {
                        // Track progress
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`Upload progress: ${progress}%`);
                    },
                    (error) => {
                        console.error('Upload error:', error);
                        alert('Upload failed. Please try again.');
                        setIsUploadingImage(false);
                    },
                    async () => {
                        // Upload complete - get download URL
                        try {
                            const downloadURL = await getDownloadURL(imageRef);
                            
                            console.log('✅ Firebase URL received:', downloadURL);
                            
                            // ✅ Update mit Firebase URL (wird in DB gespeichert)
                            handleUpdate({ 
                                backgroundImage: downloadURL
                            });
                            
                            setIsUploadingImage(false);
                            console.log('✅ Background image updated with Firebase URL!');
                        } catch (error) {
                            console.error('Error getting download URL:', error);
                            alert('Failed to get image URL. Please try again.');
                            setIsUploadingImage(false);
                        }
                    }
                );
            } catch (error) {
                console.error('❌ Image upload failed:', error);
                alert('Upload failed. Please try again.');
                setIsUploadingImage(false);
            }
        };
        
        input.onerror = (error) => {
            console.error('❌ File input error:', error);
            setIsUploadingImage(false);
        };
        
        // Trigger file picker
        input.click();
    };
    
    const handleRemoveBackgroundImage = () => {
        handleUpdate({ 
            backgroundImage: null,
            backgroundImageOpacity: 100
        });
    };
    
    const handleBackgroundImageOpacityChange = (value) => {
        handleUpdate({ backgroundImageOpacity: Number(value) });
    };
    
    const handleBackgroundImageSizeChange = (size) => {
        handleUpdate({ backgroundImageSize: size });
    };
    
    const handleBackgroundImagePositionChange = (position) => {
        handleUpdate({ backgroundImagePosition: position });
    };
    
    const updateColorSlot = (index, color) => {
        const newSlots = [...customColorSlots];
        newSlots[index] = color;
        setCustomColorSlots(newSlots);
    };
    
    // 🎨 Toggle Section
    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };
    
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`layout-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div className="layout-toolbar">
                {/* 🎨 MAIN TOOLBAR BUTTONS - oben fixiert */}
                <div className="layout-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="layout-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* Background Section */}
                    <button
                        className={`layout-toolbar-btn ${expandedSection === 'background' ? 'is-active' : ''}`}
                        onClick={() => toggleSection('background')}
                        title="Background (Color, Gradient, Image)"
                    >
                        <i className="fas fa-fill-drip"></i>
                    </button>
                    
                    {/* Spacing Section (Padding + Margin) */}
                    <button
                        className={`layout-toolbar-btn ${expandedSection === 'spacing' ? 'is-active' : ''}`}
                        onClick={() => toggleSection('spacing')}
                        title="Spacing (Padding & Margin)"
                    >
                        <i className="fas fa-expand-arrows-alt"></i>
                    </button>
                    
                    {/* Dimensions Section (Width, Height, Min-Width, Min-Height) */}
                    <button
                        className={`layout-toolbar-btn ${expandedSection === 'dimensions' ? 'is-active' : ''}`}
                        onClick={() => toggleSection('dimensions')}
                        title="Dimensions (Width, Height, Min-Width, Min-Height)"
                    >
                        <i className="fas fa-ruler-combined"></i>
                    </button>
                    
                    {/* Layout Section */}
                    <button
                        className={`layout-toolbar-btn ${expandedSection === 'layout' ? 'is-active' : ''}`}
                        onClick={() => toggleSection('layout')}
                        title="Layout"
                    >
                        <i className="fas fa-th-large"></i>
                    </button>
                    
                    {/* Style Section (Border) */}
                    <button
                        className={`layout-toolbar-btn ${expandedSection === 'style' ? 'is-active' : ''}`}
                        onClick={() => toggleSection('style')}
                        title="Style (Border)"
                    >
                        <i className="fas fa-paint-brush"></i>
                    </button>
                    
                    {/* 🎯 Close Button */}
                    <button
                        className="layout-close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            
            {/* 📦 Expanded Sections */}
            
                {/* 🎨 EXPANDABLE SECTIONS - klappen nach unten auf */}
                
                {/* 1️⃣ BACKGROUND SECTION - Color, Gradient, Image */}
                {expandedSection === 'background' && (
                    <div className="layout-expanded-section">
                    
                    {/* Brand Colors */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">Brand Colors</label>
                        <div className="layout-brand-colors">
                            {Object.entries(colors).slice(0, 5).map(([key, color]) => (
                                <button
                                    key={key}
                                    className={`layout-color-btn ${!backgroundGradientEnabled && backgroundColor === color ? 'active' : ''}`}
                                    style={{ 
                                        background: color,
                                        border: backgroundColor === color && !backgroundGradientEnabled ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
                                    }}
                                    onClick={() => handleBackgroundColorChange(color)}
                                    title={key}
                                >
                                    {!backgroundGradientEnabled && backgroundColor === color && (
                                        <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Custom Color Slots */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">Custom Colors</label>
                        <div className="layout-custom-colors">
                            {customColorSlots.map((slotColor, index) => {
                                const isEmpty = slotColor === '#ffffff';
                                const isActive = !backgroundGradientEnabled && backgroundColor === slotColor && !isEmpty;
                                
                                return (
                                    <div key={index} className="layout-custom-color-slot">
                                        <button
                                            className={`layout-color-btn ${isActive ? 'active' : ''} ${isEmpty ? 'empty' : ''}`}
                                            style={{ 
                                                background: isEmpty ? 
                                                    'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%)' : 
                                                    slotColor,
                                                backgroundSize: isEmpty ? '10px 10px' : 'auto',
                                                border: isActive ? 
                                                    '2px solid white' : 
                                                    isEmpty ? 
                                                        '1px dashed rgba(255, 255, 255, 0.3)' : 
                                                        '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                            onClick={(e) => {
                                                if (isEmpty) {
                                                    document.getElementById(`layout-color-slot-${index}`).click();
                                                } else {
                                                    handleBackgroundColorChange(slotColor);
                                                }
                                            }}
                                            onDoubleClick={() => {
                                                if (!isEmpty) {
                                                    document.getElementById(`layout-color-slot-${index}`).click();
                                                }
                                            }}
                                            title={isEmpty ? 'Add custom color' : `Custom color ${index + 1}`}
                                        >
                                            {isEmpty && (
                                                <i className="fas fa-plus" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}></i>
                                            )}
                                            {isActive && !isEmpty && (
                                                <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                            )}
                                        </button>
                                        <input
                                            id={`layout-color-slot-${index}`}
                                            type="color"
                                            value={slotColor}
                                            style={{ display: 'none' }}
                                            onChange={(e) => updateColorSlot(index, e.target.value)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    {/* 🎨 Background Opacity - für Solid Color UND Gradient */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">Opacity</label>
                        <div className="layout-slider-control">
                            <span className="layout-slider-label">
                                <i className="fas fa-adjust"></i>
                                Opacity
                            </span>
                            <input
                                type="range"
                                className="layout-slider"
                                value={backgroundOpacity}
                                onChange={(e) => handleBackgroundOpacityChange(e.target.value)}
                                min="0"
                                max="100"
                                step="5"
                            />
                            <span className="layout-slider-value">{backgroundOpacity}%</span>
                        </div>
                    </div>
                    
                    {/* Gradient Toggle */}
                    <div className="layout-section-group">
                        <div className="layout-dimension-header">
                            <label className="layout-dimension-label">
                                <i className="fas fa-palette"></i>
                                Gradient
                            </label>
                            <label className="layout-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={backgroundGradientEnabled}
                                    onChange={(e) => handleGradientToggle(e.target.checked)}
                                />
                                <span className="layout-toggle-slider"></span>
                            </label>
                        </div>
                        
                        {backgroundGradientEnabled && (
                            <>
                                {/* Gradient Color 1 */}
                                <div className="layout-gradient-color-row">
                                    <label className="layout-slider-label">Color 1</label>
                                    <input
                                        type="color"
                                        value={backgroundGradientColor1}
                                        onChange={(e) => handleGradientColor1Change(e.target.value)}
                                        className="layout-color-picker"
                                    />
                                    <span className="layout-slider-value">{backgroundGradientColor1}</span>
                                </div>
                                
                                {/* Gradient Color 2 */}
                                <div className="layout-gradient-color-row">
                                    <label className="layout-slider-label">Color 2</label>
                                    <input
                                        type="color"
                                        value={backgroundGradientColor2}
                                        onChange={(e) => handleGradientColor2Change(e.target.value)}
                                        className="layout-color-picker"
                                    />
                                    <span className="layout-slider-value">{backgroundGradientColor2}</span>
                                </div>
                                
                                {/* 🎯 Gradient Direction - IMMER sichtbar wenn Gradient aktiv */}
                                <div className="layout-section-group" style={{ marginTop: '12px' }}>
                                    <label className="layout-section-label">Gradient Direction</label>
                                    <div className="layout-button-grid">
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-bottom' || backgroundGradientDirection === 'vertical' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-bottom')}
                                            title="Top to Bottom (↓)"
                                        >
                                            <i className="fas fa-arrow-down"></i>
                                            <span>↓ Bottom</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-right' || backgroundGradientDirection === 'horizontal' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-right')}
                                            title="Left to Right (→)"
                                        >
                                            <i className="fas fa-arrow-right"></i>
                                            <span>→ Right</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-top' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-top')}
                                            title="Bottom to Top (↑)"
                                        >
                                            <i className="fas fa-arrow-up"></i>
                                            <span>↑ Top</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-left' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-left')}
                                            title="Right to Left (←)"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                            <span>← Left</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-bottom-right' || backgroundGradientDirection === 'diagonal-left' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-bottom-right')}
                                            title="Diagonal ↘"
                                        >
                                            <i className="fas fa-arrow-right" style={{ transform: 'rotate(45deg)' }}></i>
                                            <span>↘ Diagonal</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-bottom-left' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-bottom-left')}
                                            title="Diagonal ↙"
                                        >
                                            <i className="fas fa-arrow-left" style={{ transform: 'rotate(-45deg)' }}></i>
                                            <span>↙ Diagonal</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-top-right' || backgroundGradientDirection === 'diagonal-right' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-top-right')}
                                            title="Diagonal ↗"
                                        >
                                            <i className="fas fa-arrow-right" style={{ transform: 'rotate(-45deg)' }}></i>
                                            <span>↗ Diagonal</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundGradientDirection === 'to-top-left' ? 'active' : ''}`}
                                            onClick={() => handleGradientDirectionChange('to-top-left')}
                                            title="Diagonal ↖"
                                        >
                                            <i className="fas fa-arrow-left" style={{ transform: 'rotate(45deg)' }}></i>
                                            <span>↖ Diagonal</span>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Gradient Animation Toggle */}
                                <div className="layout-dimension-header" style={{ marginTop: '10px' }}>
                                    <label className="layout-dimension-label">
                                        <i className="fas fa-magic"></i>
                                        Gradient Animation
                                    </label>
                                    <label className="layout-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={backgroundGradientAnimation}
                                            onChange={(e) => handleGradientAnimationToggle(e.target.checked)}
                                        />
                                        <span className="layout-toggle-slider"></span>
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Background Image */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">Background Image</label>
                        
                        {backgroundImage ? (
                            <>
                                {/* Image Preview */}
                                <div className="layout-image-preview">
                                    <img src={backgroundImage} alt="Background" />
                                    <button
                                        className="layout-remove-image-btn"
                                        onClick={handleRemoveBackgroundImage}
                                        title="Remove image"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                
                                {/* Image Opacity */}
                                <div className="layout-slider-control">
                                    <span className="layout-slider-label">
                                        <i className="fas fa-adjust"></i>
                                        Opacity
                                    </span>
                                    <input
                                        type="range"
                                        className="layout-slider"
                                        value={backgroundImageOpacity}
                                        onChange={(e) => handleBackgroundImageOpacityChange(e.target.value)}
                                        min="10"
                                        max="100"
                                        step="5"
                                    />
                                    <span className="layout-slider-value">{backgroundImageOpacity}%</span>
                                </div>
                                
                                {/* Image Size */}
                                <div className="layout-button-group">
                                    <label className="layout-section-label">Image Size</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            className={`layout-grid-btn ${backgroundImageSize === 'cover' ? 'active' : ''}`}
                                            onClick={() => handleBackgroundImageSizeChange('cover')}
                                            title="Cover (Fill)"
                                        >
                                            Cover
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundImageSize === 'contain' ? 'active' : ''}`}
                                            onClick={() => handleBackgroundImageSizeChange('contain')}
                                            title="Contain (Fit)"
                                        >
                                            Contain
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${backgroundImageSize === 'auto' ? 'active' : ''}`}
                                            onClick={() => handleBackgroundImageSizeChange('auto')}
                                            title="Original Size"
                                        >
                                            Auto
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Image Position */}
                                <div className="layout-button-group">
                                    <label className="layout-section-label">Image Position</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
                                        {['top left', 'top', 'top right', 'left', 'center', 'right', 'bottom left', 'bottom', 'bottom right'].map((pos) => (
                                            <button
                                                key={pos}
                                                className={`layout-grid-btn ${backgroundImagePosition === pos ? 'active' : ''}`}
                                                onClick={() => handleBackgroundImagePositionChange(pos)}
                                                title={pos}
                                                style={{ fontSize: '10px', padding: '6px' }}
                                            >
                                                {pos.split(' ').map(p => p[0].toUpperCase()).join('')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* Upload Button */
                            <button
                                className="layout-upload-btn"
                                onClick={handleBackgroundImageUpload}
                                disabled={isUploadingImage}
                            >
                                <i className={`fas ${isUploadingImage ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                                {isUploadingImage ? 'Uploading...' : 'Upload Background Image'}
                            </button>
                        )}
                    </div>
                    
                    <span className="layout-section-label">Background</span>
                </div>
                )}
                
                {/* 2️⃣ SPACING SECTION - Padding & Margin */}
                {expandedSection === 'spacing' && (
                    <div className="layout-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Sub-Tabs: Padding / Margin */}
                    <div className="layout-spacing-tabs">
                        <button
                            className={`layout-spacing-tab ${spacingType === 'padding' ? 'active' : ''}`}
                            onClick={() => setSpacingType('padding')}
                        >
                            <i className="fas fa-expand-arrows-alt"></i>
                            Padding
                        </button>
                        <button
                            className={`layout-spacing-tab ${spacingType === 'margin' ? 'active' : ''}`}
                            onClick={() => setSpacingType('margin')}
                        >
                            <i className="fas fa-arrows-alt"></i>
                            Margin
                        </button>
                    </div>
                    
                    {/* Padding Controls */}
                    {spacingType === 'padding' && (
                        <>
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">↑ Top</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activePaddingTop}
                                    onChange={(e) => handlePaddingChange('top', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activePaddingTop}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">→ Right</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activePaddingRight}
                                    onChange={(e) => handlePaddingChange('right', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activePaddingRight}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">↓ Bottom</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activePaddingBottom}
                                    onChange={(e) => handlePaddingChange('bottom', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activePaddingBottom}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">← Left</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activePaddingLeft}
                                    onChange={(e) => handlePaddingChange('left', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activePaddingLeft}px</span>
                            </div>
                        </>
                    )}
                    
                    {/* Margin Controls */}
                    {spacingType === 'margin' && (
                        <>
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">↑ Top</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMarginTop}
                                    onChange={(e) => handleMarginChange('top', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activeMarginTop}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">→ Right</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMarginRight}
                                    onChange={(e) => handleMarginChange('right', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activeMarginRight}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">↓ Bottom</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMarginBottom}
                                    onChange={(e) => handleMarginChange('bottom', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activeMarginBottom}px</span>
                            </div>
                            
                            <div className="layout-slider-control">
                                <span className="layout-slider-label">← Left</span>
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMarginLeft}
                                    onChange={(e) => handleMarginChange('left', e.target.value)}
                                    min="0"
                                    max="500"
                                />
                                <span className="layout-slider-value">{activeMarginLeft}px</span>
                            </div>
                        </>
                    )}
                    
                    <span className="layout-section-label">Spacing</span>
                </div>
                )}
                
                {/* 3️⃣ DIMENSIONS SECTION - Width, Height, Min-Width, Min-Height */}
                {expandedSection === 'dimensions' && (
                    <div className="layout-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                    
                    {/* Width Control */}
                    <div className="layout-dimension-group">
                        <div className="layout-dimension-header">
                            <label className="layout-dimension-label">
                                <i className="fas fa-arrows-alt-h"></i>
                                Width
                            </label>
                            <label className="layout-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isWidthEnabled}
                                    onChange={(e) => handleDimensionToggle('width', e.target.checked)}
                                />
                                <span className="layout-toggle-slider"></span>
                            </label>
                        </div>
                        {isWidthEnabled && (
                            <div className="layout-slider-control">
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeWidth}
                                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                                    min="100"
                                    max="3000"
                                    step="10"
                                />
                                <span className="layout-slider-value">{activeWidth}px</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Height Control */}
                    <div className="layout-dimension-group">
                        <div className="layout-dimension-header">
                            <label className="layout-dimension-label">
                                <i className="fas fa-arrows-alt-v"></i>
                                Height
                            </label>
                            <label className="layout-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isHeightEnabled}
                                    onChange={(e) => handleDimensionToggle('height', e.target.checked)}
                                />
                                <span className="layout-toggle-slider"></span>
                            </label>
                        </div>
                        {isHeightEnabled && (
                            <div className="layout-slider-control">
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeHeight}
                                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                                    min="100"
                                    max="3000"
                                    step="10"
                                />
                                <span className="layout-slider-value">{activeHeight}px</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Min-Width Control */}
                    <div className="layout-dimension-group">
                        <div className="layout-dimension-header">
                            <label className="layout-dimension-label">
                                <i className="fas fa-compress-arrows-alt"></i>
                                Min-Width
                            </label>
                            <label className="layout-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isMinWidthEnabled}
                                    onChange={(e) => handleDimensionToggle('minWidth', e.target.checked)}
                                />
                                <span className="layout-toggle-slider"></span>
                            </label>
                        </div>
                        {isMinWidthEnabled && (
                            <div className="layout-slider-control">
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMinWidth}
                                    onChange={(e) => handleDimensionChange('minWidth', e.target.value)}
                                    min="0"
                                    max="3000"
                                    step="10"
                                />
                                <span className="layout-slider-value">{activeMinWidth}px</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Min-Height Control */}
                    <div className="layout-dimension-group">
                        <div className="layout-dimension-header">
                            <label className="layout-dimension-label">
                                <i className="fas fa-compress-alt"></i>
                                Min-Height
                            </label>
                            <label className="layout-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={isMinHeightEnabled}
                                    onChange={(e) => handleDimensionToggle('minHeight', e.target.checked)}
                                />
                                <span className="layout-toggle-slider"></span>
                            </label>
                        </div>
                        {isMinHeightEnabled && (
                            <div className="layout-slider-control">
                                <input
                                    type="range"
                                    className="layout-slider"
                                    value={activeMinHeight}
                                    onChange={(e) => handleDimensionChange('minHeight', e.target.value)}
                                    min="0"
                                    max="3000"
                                    step="10"
                                />
                                <span className="layout-slider-value">{activeMinHeight}px</span>
                            </div>
                        )}
                    </div>
                    
                    <span className="layout-section-label">Dimensions</span>
                </div>
                )}
                
                {/* 4️⃣ LAYOUT SECTION - Direction & Distribution */}
                {expandedSection === 'layout' && (
                    <div className="layout-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                    
                    {/* Flex Direction */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">Flex Direction</label>
                        <div className="layout-button-grid">
                            <button
                                className={`layout-grid-btn ${activeFlexDirection === 'row' ? 'active' : ''}`}
                                onClick={() => handleFlexDirectionChange('row')}
                                title="Row (Horizontal)"
                            >
                                <i className="fas fa-arrows-alt-h"></i>
                                <span>Row</span>
                            </button>
                            <button
                                className={`layout-grid-btn ${activeFlexDirection === 'column' ? 'active' : ''}`}
                                onClick={() => handleFlexDirectionChange('column')}
                                title="Column (Vertical)"
                            >
                                <i className="fas fa-arrows-alt-v"></i>
                                <span>Column</span>
                            </button>
                            <button
                                className={`layout-grid-btn ${activeFlexDirection === 'row-reverse' ? 'active' : ''}`}
                                onClick={() => handleFlexDirectionChange('row-reverse')}
                                title="Row Reverse"
                            >
                                <i className="fas fa-exchange-alt"></i>
                                <span>Row Rev</span>
                            </button>
                            <button
                                className={`layout-grid-btn ${activeFlexDirection === 'column-reverse' ? 'active' : ''}`}
                                onClick={() => handleFlexDirectionChange('column-reverse')}
                                title="Column Reverse"
                            >
                                <i className="fas fa-sort"></i>
                                <span>Col Rev</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* Child Distribution */}
                    <div className="layout-section-group">
                        <label className="layout-section-label">
                            Child Distribution 
                            {containerCount > 0 && (
                                <span className="layout-child-count"> ({containerCount} {containerCount === 1 ? 'child' : 'children'})</span>
                            )}
                        </label>
                        
                        {/* ✅ Single Child (1 Container) - Verschiedene Breiten */}
                        {containerCount === 1 && (
                            <>
                                {/* Width Controls */}
                                <div className="layout-button-grid layout-distribution-grid" style={{marginBottom: '12px'}}>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '100' || activeChildDistribution === 'auto' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('100')}
                                        title="100% Width (Full)"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>100%</span>
                                    </button>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '90' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('90')}
                                        title="90% Width"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>90%</span>
                                    </button>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '80' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('80')}
                                        title="80% Width"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>80%</span>
                                    </button>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '70' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('70')}
                                        title="70% Width"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>70%</span>
                                    </button>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '60' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('60')}
                                        title="60% Width"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>60%</span>
                                    </button>
                                    <button
                                        className={`layout-grid-btn ${activeChildDistribution === '50' ? 'active' : ''}`}
                                        onClick={() => handleChildDistributionChange('50')}
                                        title="50% Width"
                                    >
                                        <div className="distribution-visual">
                                            <div style={{width: '100%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        </div>
                                        <span>50%</span>
                                    </button>
                                </div>
                                
                                {/* ✅ Horizontal Alignment für Single Container */}
                                <div className="layout-section-group" style={{marginTop: '12px'}}>
                                    <label className="layout-section-label">Container Alignment</label>
                                    <div className="layout-button-grid">
                                        <button
                                            className={`layout-grid-btn ${(device === 'desktop' ? (flexDirection || 'row') : (flexDirectionMobile || flexDirection || 'row')) === 'row-left' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (device === 'desktop') {
                                                    handleFlexDirectionChange('row-left');
                                                } else {
                                                    handleUpdate({ flexDirectionMobile: 'row-left' });
                                                }
                                            }}
                                            title="Align Left"
                                        >
                                            <i className="fas fa-align-left"></i>
                                            <span>Left</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${(device === 'desktop' ? (flexDirection || 'row') : (flexDirectionMobile || flexDirection || 'row')) === 'row' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (device === 'desktop') {
                                                    handleFlexDirectionChange('row');
                                                } else {
                                                    handleUpdate({ flexDirectionMobile: 'row' });
                                                }
                                            }}
                                            title="Align Center"
                                        >
                                            <i className="fas fa-align-center"></i>
                                            <span>Center</span>
                                        </button>
                                        <button
                                            className={`layout-grid-btn ${(device === 'desktop' ? (flexDirection || 'row') : (flexDirectionMobile || flexDirection || 'row')) === 'row-right' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (device === 'desktop') {
                                                    handleFlexDirectionChange('row-right');
                                                } else {
                                                    handleUpdate({ flexDirectionMobile: 'row-right' });
                                                }
                                            }}
                                            title="Align Right"
                                        >
                                            <i className="fas fa-align-right"></i>
                                            <span>Right</span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* ✅ Two Containers (2 Container) - 50/50, 60/40, etc. */}
                        {containerCount === 2 && (
                            <div className="layout-button-grid layout-distribution-grid">
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '50-50' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('50-50')}
                                    title="50% / 50%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '50%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '50%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>50/50</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '60-40' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('60-40')}
                                    title="60% / 40%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '60%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '40%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>60/40</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '40-60' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('40-60')}
                                    title="40% / 60%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '40%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '60%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>40/60</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '70-30' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('70-30')}
                                    title="70% / 30%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '70%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '30%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>70/30</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '30-70' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('30-70')}
                                    title="30% / 70%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '30%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '70%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>30/70</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === 'auto' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('auto')}
                                    title="Auto (Equal)"
                                >
                                    <div className="distribution-visual">
                                        <div style={{flex: 1, height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{flex: 1, height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                    </div>
                                    <span>Auto</span>
                                </button>
                            </div>
                        )}
                        
                        {/* ✅ Info wenn keine Container vorhanden */}
                        {containerCount === 0 && (
                            <div className="layout-info-message">
                                <i className="fas fa-info-circle"></i>
                                <span>No containers available. Add a container first.</span>
                            </div>
                        )}
                    </div>
                    
                        {/* ✅ Three Containers (3 Container) - 33/33/33, 50/25/25, etc. */}
                        {containerCount === 3 && (
                            <div className="layout-button-grid layout-distribution-grid">
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '33-33-33' || activeChildDistribution === 'auto' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('33-33-33')}
                                    title="33% / 33% / 33%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '33.33%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '33.33%', height: '20px', background: 'currentColor', opacity: 0.4}}></div>
                                        <div style={{width: '33.33%', height: '20px', background: 'currentColor', opacity: 0.2}}></div>
                                    </div>
                                    <span>Equal</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '50-25-25' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('50-25-25')}
                                    title="50% / 25% / 25%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '50%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.4}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.2}}></div>
                                    </div>
                                    <span>50/25/25</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '25-50-25' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('25-50-25')}
                                    title="25% / 50% / 25%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '50%', height: '20px', background: 'currentColor', opacity: 0.4}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.2}}></div>
                                    </div>
                                    <span>25/50/25</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '25-25-50' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('25-25-50')}
                                    title="25% / 25% / 50%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.4}}></div>
                                        <div style={{width: '50%', height: '20px', background: 'currentColor', opacity: 0.2}}></div>
                                    </div>
                                    <span>25/25/50</span>
                                </button>
                            </div>
                        )}
                        
                        {/* ✅ Four Containers (4 Container) - Equal, 40/20/20/20 */}
                        {containerCount === 4 && (
                            <div className="layout-button-grid layout-distribution-grid">
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '25-25-25-25' || activeChildDistribution === 'auto' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('25-25-25-25')}
                                    title="25% / 25% / 25% / 25%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.7}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.5}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                        <div style={{width: '25%', height: '20px', background: 'currentColor', opacity: 0.1}}></div>
                                    </div>
                                    <span>Equal</span>
                                </button>
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '40-20-20-20' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('40-20-20-20')}
                                    title="40% / 20% / 20% / 20%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '40%', height: '20px', background: 'currentColor', opacity: 0.7}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.5}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.3}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.1}}></div>
                                    </div>
                                    <span>40/20/20/20</span>
                                </button>
                            </div>
                        )}
                        
                        {/* ✅ Five Containers (5 Container) - Equal */}
                        {containerCount === 5 && (
                            <div className="layout-button-grid layout-distribution-grid">
                                <button
                                    className={`layout-grid-btn ${activeChildDistribution === '20-20-20-20-20' || activeChildDistribution === 'auto' ? 'active' : ''}`}
                                    onClick={() => handleChildDistributionChange('20-20-20-20-20')}
                                    title="20% / 20% / 20% / 20% / 20%"
                                >
                                    <div className="distribution-visual">
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.8}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.6}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.4}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.2}}></div>
                                        <div style={{width: '20%', height: '20px', background: 'currentColor', opacity: 0.1}}></div>
                                    </div>
                                    <span>Equal</span>
                                </button>
                            </div>
                        )}
                    
                    <span className="layout-section-label">Layout</span>
                </div>
                )}
                
                {/* 5️⃣ STYLE SECTION - Border Settings */}
                {expandedSection === 'style' && (
                    <div className="layout-expanded-section">
                        
                        {/* Border Top */}
                        <div className="layout-section-group">
                            <div className="layout-dimension-header">
                                <label className="layout-dimension-label">
                                    <i className="fas fa-arrow-up"></i>
                                    Border Top
                                </label>
                                <label className="layout-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={borderTopEnabled}
                                        onChange={(e) => handleUpdate({ borderTopEnabled: e.target.checked })}
                                    />
                                    <span className="layout-toggle-slider"></span>
                                </label>
                            </div>
                            {borderTopEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                    {/* Width Slider */}
                                    <div className="layout-slider-control">
                                        <span className="layout-slider-label">Width</span>
                                        <input
                                            type="range"
                                            className="layout-slider"
                                            value={borderTopWidth}
                                            onChange={(e) => handleUpdate({ borderTopWidth: Number(e.target.value) })}
                                            min="1"
                                            max="20"
                                        />
                                        <span className="layout-slider-value">{borderTopWidth}px</span>
                                    </div>
                                    {/* Brand Colors */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label className="layout-slider-label">Brand Colors</label>
                                        <div className="layout-brand-colors">
                                            {Object.entries(colors).slice(0, 5).map(([key, color]) => (
                                                <button
                                                    key={key}
                                                    className={`layout-color-btn ${borderTopColor === color ? 'active' : ''}`}
                                                    style={{ 
                                                        background: color,
                                                        border: borderTopColor === color ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}
                                                    onClick={() => handleUpdate({ borderTopColor: color })}
                                                    title={key}
                                                >
                                                    {borderTopColor === color && (
                                                        <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Custom Color Picker */}
                                    <div className="layout-gradient-color-row">
                                        <label className="layout-slider-label">Custom</label>
                                        <input
                                            type="color"
                                            value={borderTopColor}
                                            onChange={(e) => handleUpdate({ borderTopColor: e.target.value })}
                                            className="layout-color-picker"
                                        />
                                        <span className="layout-slider-value">{borderTopColor}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Border Bottom */}
                        <div className="layout-section-group">
                            <div className="layout-dimension-header">
                                <label className="layout-dimension-label">
                                    <i className="fas fa-arrow-down"></i>
                                    Border Bottom
                                </label>
                                <label className="layout-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={borderBottomEnabled}
                                        onChange={(e) => handleUpdate({ borderBottomEnabled: e.target.checked })}
                                    />
                                    <span className="layout-toggle-slider"></span>
                                </label>
                            </div>
                            {borderBottomEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                    {/* Width Slider */}
                                    <div className="layout-slider-control">
                                        <span className="layout-slider-label">Width</span>
                                        <input
                                            type="range"
                                            className="layout-slider"
                                            value={borderBottomWidth}
                                            onChange={(e) => handleUpdate({ borderBottomWidth: Number(e.target.value) })}
                                            min="1"
                                            max="20"
                                        />
                                        <span className="layout-slider-value">{borderBottomWidth}px</span>
                                    </div>
                                    {/* Brand Colors */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label className="layout-slider-label">Brand Colors</label>
                                        <div className="layout-brand-colors">
                                            {Object.entries(colors).slice(0, 5).map(([key, color]) => (
                                                <button
                                                    key={key}
                                                    className={`layout-color-btn ${borderBottomColor === color ? 'active' : ''}`}
                                                    style={{ 
                                                        background: color,
                                                        border: borderBottomColor === color ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}
                                                    onClick={() => handleUpdate({ borderBottomColor: color })}
                                                    title={key}
                                                >
                                                    {borderBottomColor === color && (
                                                        <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Custom Color Picker */}
                                    <div className="layout-gradient-color-row">
                                        <label className="layout-slider-label">Custom</label>
                                        <input
                                            type="color"
                                            value={borderBottomColor}
                                            onChange={(e) => handleUpdate({ borderBottomColor: e.target.value })}
                                            className="layout-color-picker"
                                        />
                                        <span className="layout-slider-value">{borderBottomColor}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Border Left */}
                        <div className="layout-section-group">
                            <div className="layout-dimension-header">
                                <label className="layout-dimension-label">
                                    <i className="fas fa-arrow-left"></i>
                                    Border Left
                                </label>
                                <label className="layout-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={borderLeftEnabled}
                                        onChange={(e) => handleUpdate({ borderLeftEnabled: e.target.checked })}
                                    />
                                    <span className="layout-toggle-slider"></span>
                                </label>
                            </div>
                            {borderLeftEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                    {/* Width Slider */}
                                    <div className="layout-slider-control">
                                        <span className="layout-slider-label">Width</span>
                                        <input
                                            type="range"
                                            className="layout-slider"
                                            value={borderLeftWidth}
                                            onChange={(e) => handleUpdate({ borderLeftWidth: Number(e.target.value) })}
                                            min="1"
                                            max="20"
                                        />
                                        <span className="layout-slider-value">{borderLeftWidth}px</span>
                                    </div>
                                    {/* Brand Colors */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label className="layout-slider-label">Brand Colors</label>
                                        <div className="layout-brand-colors">
                                            {Object.entries(colors).slice(0, 5).map(([key, color]) => (
                                                <button
                                                    key={key}
                                                    className={`layout-color-btn ${borderLeftColor === color ? 'active' : ''}`}
                                                    style={{ 
                                                        background: color,
                                                        border: borderLeftColor === color ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}
                                                    onClick={() => handleUpdate({ borderLeftColor: color })}
                                                    title={key}
                                                >
                                                    {borderLeftColor === color && (
                                                        <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Custom Color Picker */}
                                    <div className="layout-gradient-color-row">
                                        <label className="layout-slider-label">Custom</label>
                                        <input
                                            type="color"
                                            value={borderLeftColor}
                                            onChange={(e) => handleUpdate({ borderLeftColor: e.target.value })}
                                            className="layout-color-picker"
                                        />
                                        <span className="layout-slider-value">{borderLeftColor}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Border Right */}
                        <div className="layout-section-group">
                            <div className="layout-dimension-header">
                                <label className="layout-dimension-label">
                                    <i className="fas fa-arrow-right"></i>
                                    Border Right
                                </label>
                                <label className="layout-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={borderRightEnabled}
                                        onChange={(e) => handleUpdate({ borderRightEnabled: e.target.checked })}
                                    />
                                    <span className="layout-toggle-slider"></span>
                                </label>
                            </div>
                            {borderRightEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                    {/* Width Slider */}
                                    <div className="layout-slider-control">
                                        <span className="layout-slider-label">Width</span>
                                        <input
                                            type="range"
                                            className="layout-slider"
                                            value={borderRightWidth}
                                            onChange={(e) => handleUpdate({ borderRightWidth: Number(e.target.value) })}
                                            min="1"
                                            max="20"
                                        />
                                        <span className="layout-slider-value">{borderRightWidth}px</span>
                                    </div>
                                    {/* Brand Colors */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <label className="layout-slider-label">Brand Colors</label>
                                        <div className="layout-brand-colors">
                                            {Object.entries(colors).slice(0, 5).map(([key, color]) => (
                                                <button
                                                    key={key}
                                                    className={`layout-color-btn ${borderRightColor === color ? 'active' : ''}`}
                                                    style={{ 
                                                        background: color,
                                                        border: borderRightColor === color ? '2px solid white' : '1px solid rgba(255, 255, 255, 0.2)'
                                                    }}
                                                    onClick={() => handleUpdate({ borderRightColor: color })}
                                                    title={key}
                                                >
                                                    {borderRightColor === color && (
                                                        <i className="fas fa-check" style={{ color: 'white', fontSize: '12px' }}></i>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Custom Color Picker */}
                                    <div className="layout-gradient-color-row">
                                        <label className="layout-slider-label">Custom</label>
                                        <input
                                            type="color"
                                            value={borderRightColor}
                                            onChange={(e) => handleUpdate({ borderRightColor: e.target.value })}
                                            className="layout-color-picker"
                                        />
                                        <span className="layout-slider-value">{borderRightColor}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="layout-section-label">Style</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default LayoutFloatingToolbar;

